import { useCheckoutFrameStore } from 'app/context/checkoutStore';
import { useAnalytics } from 'app/hooks/useAnalytics';
import { apiAmountToCurrencyString } from 'app/util/currency';
import { isProductionEnv } from 'app/util/env';
import { createNanoEvents } from 'nanoevents';
import { useIsMobile } from '../../hooks/useIsMobile';
import { checkEnroll, generateJWT } from '../../http/threeDS';
import { getCardinal, startSetupCardinal } from '../../util/cardinal';
import {
  checkIfRequiresChallenge,
  describeAuthentication,
  emitError,
  processChallenge,
  setupTestCcaFallback,
  setupValidateListener,
} from './challengeHelpers';
import { CardinalEvents, THREE_DS_FLOW_TIMEOUT_MS, TwoFAEvents } from './constants';
import { getBrowserInformation, getDeviceDataCollection, markDdcFailed, markDdcStarted } from './deviceData';
import { getCardinalInitialData, parseDataToCreateJWTDto } from './helpers';
import { ValidationOrderData } from './interface';
import { useErrorStore } from 'app/context/errorStore';
import { logError } from 'app/util/errorsLoggerWrapper';
import { ThreeDSStep, create3DSTracer, traceSetupStep } from './trace';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Whether the challenge frame ever painted is what separates "the customer never saw it" from
 * "the customer authenticated and the response was lost" -- the two stall shapes look
 * identical from the backend.
 */
const traceChallengeUi = (cardinal: any, tracer: ReturnType<typeof create3DSTracer>) => {
  const onRender = () => tracer.step(ThreeDSStep.CHALLENGE_UI_RENDERED);
  const onClose = () => tracer.step(ThreeDSStep.CHALLENGE_UI_CLOSED);
  cardinal.on(CardinalEvents.UI_RENDER, onRender);
  cardinal.on(CardinalEvents.UI_CLOSE, onClose);
  return () => {
    try {
      cardinal.off(CardinalEvents.UI_RENDER, onRender);
      cardinal.off(CardinalEvents.UI_CLOSE, onClose);
    } catch (e) {
      logError('3DS: could not remove challenge UI trace listeners', { message: (e as Error)?.message });
    }
  };
};

const TIMEOUT_SOURCE = 'execute: timed out waiting for the 3DS result';
const DISPOSE_SOURCE = 'execute: disposed while the flow was still running';

/**
 * A rejection racing the flow's own promise. `cancel` matters as much as the timer: without
 * it a resolved flow would hold a ten minute handle, and in tests the pending timer outlives
 * the case that created it.
 */
const createFlowTimeout = (timeoutMs: number, message: string) => {
  let timer: ReturnType<typeof setTimeout>;
  const promise = new Promise<never>((_, reject) => {
    // Object.assign, not `const error: any`, so the rejection is statically an Error: the
    // value was one all along, and the `any` was the only reason a linter could not tell.
    // Same runtime object, same enumerable `source`, so nothing downstream changes.
    timer = setTimeout(() => reject(Object.assign(new Error(message), { source: TIMEOUT_SOURCE })), timeoutMs);
  });
  return { promise, cancel: () => clearTimeout(timer) };
};

export const use3DSFlow = () => {
  const { setError } = useErrorStore();
  const { checkoutRequest, entity } = useCheckoutFrameStore();
  const { sendStart3DSDatalayer, sendFinish3DSValidationDatalayer } = useAnalytics();
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  // Lets dispose() end whichever flow is running instead of clearing every listener
  // registered on the shared window.Cardinal.
  const abortActiveFlow = useRef<(() => void) | null>(null);

  const validate3DSRequiredData = () => {
    if (!checkoutRequest) {
      throw new Error('checkoutRequest is undefined at init cardinal moment');
    }
    if (!entity) {
      throw new Error('entity is undefined at init cardinal moment');
    }
  };

  const execute = async (customerInfo: any): Promise<any> => {
    validate3DSRequiredData();
    const localEmitter = createNanoEvents();
    let unsubscribers: any = {};
    let requireChallenge = false;
    const tracer = create3DSTracer({
      checkoutId: checkoutRequest?.id,
      chargeId: checkoutRequest?.chargeId,
      entityName: entity?.name ?? null,
      companyId: checkoutRequest?.companyId ?? null,
      liveMode: checkoutRequest?.liveMode,
      isMobile,
    });
    try {
      const { amount, id, tokenId, orderTemplate, checkoutAffiliation, liveMode, chargeId } = checkoutRequest!;
      const { affiliation, acquirer } = checkoutAffiliation;
      const { name, phone } = orderTemplate.customerInfo;
      const cardinal = getCardinal();
      const { transactionData, cardToValidate } = getCardinalInitialData({
        affiliation,
        chargeId,
        serviceAcquirer: acquirer,
        amount: apiAmountToCurrencyString(amount ?? 0),
        customerInfo,
        referenceId: id,
        tokenID: tokenId,
        phoneNumber: phone,
        customerName: name,
        liveMode,
        entity: entity!,
        isMobile,
      });

      tracer.enrich({ affiliation, acquirer });
      unsubscribers.unsubscribeValidated = setupValidateListener(
        cardinal,
        cardToValidate,
        localEmitter,
        affiliation,
        acquirer,
        tracer,
      );
      unsubscribers.unsubscribeChallengeUi = traceChallengeUi(cardinal, tracer);
      // Rejects the flow rather than tearing it down directly: teardown alone would remove
      // the listeners AND cancel the timeout, leaving nothing able to settle the promise, so
      // execute() would hang exactly as it did before there was a timeout. Going through the
      // emitter means the catch/finally below runs, which cleans up once and traces why.
      abortActiveFlow.current = () => {
        const error: any = new Error(t('errors.internal'));
        error.source = DISPOSE_SOURCE;
        tracer.fail(ThreeDSStep.ERROR_EMITTED, { error_source: DISPOSE_SOURCE });
        localEmitter.emit(TwoFAEvents.ERROR, error);
      };

      // Fuera de produccion el fallback siempre se registra; en produccion solo en modo de pruebas.
      if (!isProductionEnv() || !liveMode) {
        unsubscribers.unsubscribeTestCcaFallback = setupTestCcaFallback(
          cardinal,
          cardToValidate,
          localEmitter,
          affiliation,
          acquirer,
          tracer,
        );
      }

      const flowTimeout = createFlowTimeout(THREE_DS_FLOW_TIMEOUT_MS, t('errors.threeDsTimeout'));
      unsubscribers.cancelFlowTimeout = flowTimeout.cancel;

      const orderPromise: Promise<ValidationOrderData> = new Promise((resolve, reject) => {
        unsubscribers.unsubscribeError = localEmitter.on(TwoFAEvents.ERROR, (error) => reject(error));
        unsubscribers.unsubscribeCreateOrder = localEmitter.on(
          TwoFAEvents.CREATE_ORDER,
          (additionalOrderData: ValidationOrderData) => {
            resolve(additionalOrderData);
          },
        );
      });
      // Raced here rather than at the await below: check-enroll carries no timeout of its own,
      // so the bound has to cover a stalled enrollment too, and a rejection firing while
      // nothing is attached yet would surface as an unhandled rejection.
      const settledFlow = Promise.race([orderPromise, flowTimeout.promise]);
      const runEnrollment = async () => {
        try {
          tracer.step(ThreeDSStep.CHECK_ENROLL_REQUEST);
          const ddc = getDeviceDataCollection();
          const enrollmentData = {
            ...transactionData,
            browserInformation: getBrowserInformation(),
            ...(ddc && { ddc }),
          };
          const checkEnrollData = (await checkEnroll({ data: enrollmentData })).objectResponse;
          tracer.step(ThreeDSStep.CHECK_ENROLL_RESPONSE, describeAuthentication(checkEnrollData));
          requireChallenge = checkIfRequiresChallenge(localEmitter, checkEnrollData, tracer);
          tracer.enrich({ requireChallenge, authTransactionId: checkEnrollData?.authenticationTransactionId });
          sendStart3DSDatalayer(requireChallenge);
          processChallenge(cardinal, affiliation, acquirer, checkEnrollData, localEmitter, requireChallenge, tracer);
        } catch (error) {
          emitError(localEmitter, 'handleSetupComplete: Unexpected error occurred', error, tracer);
        }
      };

      // Deliberately not awaited. Awaiting it here would put the enrollment call ahead of the
      // timeout in the only place execute() blocks, and check-enroll has no timeout of its
      // own -- a stalled request would then hang the flow exactly as a lost challenge does.
      // Everything it produces reaches this function through localEmitter anyway.
      runEnrollment().catch((error) => emitError(localEmitter, 'execute: enrollment failed', error, tracer));

      const orderValidationRespose = await settledFlow;
      tracer.settle(ThreeDSStep.EXECUTE_RESOLVED, { require_challenge: requireChallenge });
      sendFinish3DSValidationDatalayer(true, requireChallenge);
      return { ...orderValidationRespose, requireChallenge, threeDSResult: 'success' };
    } catch (e: any) {
      tracer.settle(ThreeDSStep.EXECUTE_REJECTED, { error_source: e?.source ?? 'unknown' }, e);
      throw e;
    } finally {
      tracer.step(ThreeDSStep.LISTENERS_DISPOSED);
      tracer.dispose();
      abortActiveFlow.current = null;
      cleanup(unsubscribers);
    }
  };

  const initCardinal = async () => {
    if (!checkoutRequest) {
      throw { source: 'checkoutRequest is undefined at generateJWT moment', data: null };
    }
    const { amount, id, orderTemplate, name, liveMode } = checkoutRequest;
    const setupContext = {
      checkoutId: id,
      chargeId: checkoutRequest.chargeId ?? null,
      entityName: entity?.name ?? null,
      companyId: checkoutRequest.companyId ?? null,
      liveMode,
    };
    try {
      traceSetupStep(ThreeDSStep.SETUP_JWT_REQUEST, setupContext);
      const jwt = await generateJWT({
        data: parseDataToCreateJWTDto({
          amount: amount.toString() ?? '0',
          id,
          customerName: orderTemplate?.customerInfo?.name ?? '',
          name,
          liveMode,
        }),
      });
      traceSetupStep(ThreeDSStep.SETUP_JWT_RECEIVED, setupContext);
      markDdcStarted();
      startSetupCardinal(jwt);
      // Cardinal accepting setup() says nothing about it completing: SETUP_COMPLETE arrives
      // later, or never. A setup_started with no setup_complete is a payment that never got
      // as far as arming the submit button.
      traceSetupStep(ThreeDSStep.SETUP_STARTED, setupContext);
    } catch (error: any) {
      markDdcFailed();
      traceSetupStep(ThreeDSStep.SETUP_FAILED, setupContext, error);
      setError(error);
    }
  };

  const cleanup = (unsubscribers: any) => {
    unsubscribers.cancelFlowTimeout?.();
    unsubscribers.unsubscribeValidated?.();
    unsubscribers.unsubscribeError?.();
    unsubscribers.unsubscribeCreateOrder?.();
    unsubscribers.unsubscribeTestCcaFallback?.();
    unsubscribers.unsubscribeChallengeUi?.();
  };

  /**
   * Ends the running flow, if any, and lets its own teardown detach exactly what it
   * registered. It used to call cardinal.off(event) with no handler, which on the shared
   * singleton also removed the listeners belonging to useThreeDsFlow -- and, when this ran
   * mid-challenge, the very listener the in-flight authentication was going to come back to.
   */
  const dispose = () => {
    abortActiveFlow.current?.();
    abortActiveFlow.current = null;
  };

  return { dispose, execute, initCardinal };
};
